import {useUncontrolled} from '@mantine/hooks';
import React from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import AbsoluteDropdownContainer from '../AbsoluteDropdownContainer';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from '../buttons/Button';
import Menu, {MenuProps} from './Menu';
import Option from './Option';
import Section from './Section';

type OverflowButtonProps = Partial<ButtonProps> & {
  enableTooltip: boolean;
  tooltipPlacement?: FloatingPlacement;
  isIconHorizontal?: boolean;
};

export function OverflowButton(props: OverflowButtonProps): JSX.Element {
  const {enableTooltip, tooltipPlacement, isIconHorizontal = true, ...buttonProps} = props;
  return (
    <Tooltip isDisabled={!enableTooltip} text="More options" placement={tooltipPlacement}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon={isIconHorizontal ? 'ellipsis' : 'ellipsis-vertical'}
        size={ButtonSize.MINI}
        {...buttonProps}
      />
    </Tooltip>
  );
}

export interface OverflowMenuOption {
  icon?: BenchlingIconName;
  iconClassName?: string;
  contentClassName?: string;
  contents: JSX.Element;
  onSelect?: () => void;
  href?: string;
  openInNewTab?: boolean;
  isDisabled?: boolean;
  tooltipTitle?: string | null;
  dataTestAttrs?: DataTestAttrs;
}

export const DIVIDER = {type: 'divider' as const};
interface DividerOption {
  type: 'divider';
}

function isDivider(option: OverflowMenuOption | DividerOption): option is DividerOption {
  return (option as DividerOption).type === 'divider';
}

export interface OverflowMenuProps {
  buttonProps?: Partial<ButtonProps>;
  menuProps?: Partial<MenuProps>;
  options: Array<OverflowMenuOption | DividerOption>;

  // Passed down to OverflowButton and if set to true, a tooltip will show up with the text "More options".
  // It is false by default so that callers can opt in one-by-one. (Each caller needs to carefully check the
  // rendered output - check tooltipPlacement and make sure they're not rendering another tooltip on top.)
  // Eventually, it should be true by default (opt out).
  enableTooltip?: boolean;
  tooltipPlacement?: FloatingPlacement;
  /**
   * Passed down to OverflowButton which toggles the icon to be either a vertical or horizontal ellipsis.
   * @default true.
   */
  isIconHorizontal?: boolean;
  /** Controlled open state. If omitted, the component manages its own state. */
  isOpen?: boolean;
  /** Called when the menu opens or closes. */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * A menu for hiding superfluous buttons in a table's title bar.
 */
export default function OverflowMenu({
  buttonProps,
  menuProps,
  options,
  tooltipPlacement,
  enableTooltip = false,
  isIconHorizontal = true,
  isOpen: isOpenProp,
  onOpenChange,
}: OverflowMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useUncontrolled({
    value: isOpenProp,
    defaultValue: false,
    onChange: onOpenChange,
  });

  const sections: Array<Array<OverflowMenuOption>> = [];
  let currentSection: Array<OverflowMenuOption> = [];

  options.forEach((option) => {
    if (isDivider(option)) {
      if (currentSection.length > 0) {
        sections.push(currentSection);
        currentSection = [];
      }
    } else {
      currentSection.push(option);
    }
  });

  if (currentSection.length > 0) {
    sections.push(currentSection);
  }

  return (
    <AbsoluteDropdownContainer
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM} {...menuProps}>
          {sections.map((section, sectionIndex) => (
            <Section key={sectionIndex}>
              {section.map((option, optionIndex) => {
                const {contents, ...optionProps} = option;
                return (
                  <Option key={optionIndex} {...optionProps}>
                    {contents}
                  </Option>
                );
              })}
            </Section>
          ))}
        </Menu>
      }
      onRequestClose={() => {
        setIsOpen(false);
      }}
      open={isOpen}
      shouldCloseOnInsideClick={true}
    >
      <OverflowButton
        className="u-mini-spacing-left"
        disabled={false}
        onClick={() => {
          setIsOpen(true);
        }}
        enableTooltip={enableTooltip}
        tooltipPlacement={tooltipPlacement}
        isIconHorizontal={isIconHorizontal}
        {...buttonProps}
      />
    </AbsoluteDropdownContainer>
  );
}
